import React from "react";
import { Helmet } from "react-helmet-async";
import { BUSINESS } from "@/lib/business";

const SITE_URL = "https://www.gtasecure.com";

const DEFAULTS = {
    title: "GTA Secure Inc. — Window Films, Security Cameras & Blinds in the GTA",
    description:
        "Canadian-owned, Georgetown-based: professional security window film, camera systems, and custom blinds & shutters for homes and businesses across the Greater Toronto Area.",
    image: BUSINESS.logo,
    type: "website",
};

/**
 * SEO component for per-page meta tags. Use via:
 *   <SEO title="..." description="..." path="/contact" />
 */
export default function SEO({
    title,
    description,
    image,
    path = "",
    type,
    keywords,
    jsonLd,
}) {
    const fullTitle = title ? `${title} | GTA Secure Inc.` : DEFAULTS.title;
    const desc = description || DEFAULTS.description;
    const ogImage = image || DEFAULTS.image;
    const url = `${SITE_URL}${path || "/"}`;

    return (
        <Helmet prioritizeSeoTags>
            <title>{fullTitle}</title>
            <meta name="description" content={desc} />
            {keywords && <meta name="keywords" content={keywords} />}
            <link rel="canonical" href={url} />

            {/* Open Graph */}
            <meta property="og:site_name" content="GTA Secure Inc." />
            <meta property="og:title" content={fullTitle} />
            <meta property="og:description" content={desc} />
            <meta property="og:type" content={type || DEFAULTS.type} />
            <meta property="og:url" content={url} />
            <meta property="og:image" content={ogImage} />
            <meta property="og:image:alt" content="GTA Secure Inc. logo" />
            <meta property="og:locale" content="en_CA" />

            {/* Twitter */}
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content={fullTitle} />
            <meta name="twitter:description" content={desc} />
            <meta name="twitter:image" content={ogImage} />

            {/* Geo */}
            <meta name="geo.region" content="CA-ON" />
            <meta name="geo.placename" content="Georgetown, Ontario" />

            {jsonLd && (
                <script type="application/ld+json">
                    {JSON.stringify(jsonLd)}
                </script>
            )}
        </Helmet>
    );
}

/** Local business structured data (included on Home & Contact). */
export const LOCAL_BUSINESS_JSONLD = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    name: "GTA Secure Inc.",
    image: BUSINESS.logo,
    logo: BUSINESS.logo,
    url: SITE_URL,
    telephone: BUSINESS.phone,
    email: BUSINESS.email,
    priceRange: "$$",
    address: {
        "@type": "PostalAddress",
        streetAddress: BUSINESS.address.line1,
        addressLocality: "Georgetown",
        addressRegion: "ON",
        postalCode: "L7G 0M4",
        addressCountry: "CA",
    },
    areaServed: "Greater Toronto Area",
    openingHoursSpecification: [
        {
            "@type": "OpeningHoursSpecification",
            dayOfWeek: [
                "Monday",
                "Tuesday",
                "Wednesday",
                "Thursday",
                "Friday",
                "Saturday",
            ],
            opens: "09:00",
            closes: "19:00",
        },
    ],
    sameAs: [],
    makesOffer: [
        {
            "@type": "Offer",
            itemOffered: {
                "@type": "Service",
                name: "Residential & Commercial Safety Window Films",
            },
        },
        {
            "@type": "Offer",
            itemOffered: {
                "@type": "Service",
                name: "Security Cameras Installation",
            },
        },
        {
            "@type": "Offer",
            itemOffered: {
                "@type": "Service",
                name: "Custom Blinds & Shutters",
            },
        },
    ],
};

export const serviceJsonLd = (name, description) => ({
    "@context": "https://schema.org",
    "@type": "Service",
    serviceType: name,
    name,
    description,
    provider: {
        "@type": "LocalBusiness",
        name: "GTA Secure Inc.",
        telephone: BUSINESS.phone,
        url: SITE_URL,
    },
    areaServed: "Greater Toronto Area",
});
